/**
 * Client for the dashboard read (issue #91, child of #65 phase-plan split):
 * `GET /v1/dashboard` (backend/src/api/dashboard.ts, issue #90). JWT-only —
 * there is no guest counterpart, same tier as `/v1/me` — so this dispatches
 * `authedFetch` directly instead of the guest/authed tier split
 * `lib/training/api.ts`'s `SessionAuth` drives elsewhere.
 *
 * The wire types mirror `DashboardResponse` in backend/src/api/dashboard.ts
 * one-to-one (snake_case) — this is the on-wire contract, not a place to
 * invent a parallel shape.
 */
import { authConfig, type AuthConfig } from '@/lib/auth/config';
import { authedFetch, type FetchImpl } from '@/lib/auth/api';

export type { FetchImpl };

export type ReadinessBand = 'building' | 'on_track' | 'nearly_ready' | 'exam_ready';
export type RankKey = 'candidate' | 'specialist' | 'pilot' | 'commander';

export interface DashboardDomainBar {
  domain_key: string;
  coverage: number;
  mastery: number;
  mock_accuracy: number | null;
  score: number;
}

export interface DashboardResponse {
  ecosystem: string;
  route_key: string;
  readiness: {
    score: number;
    band: ReadinessBand;
    has_mock_history: boolean;
    domains: DashboardDomainBar[];
  };
  streak: {
    current_streak: number;
    longest_streak: number;
    freezes_available: number;
  };
  exam: {
    exam_date: string | null;
    days_remaining: number | null;
  };
  rank: {
    rank: RankKey;
    xp_into_rank: number;
    xp_to_next_rank: number | null;
    next_rank: RankKey | null;
    total_xp: number;
  };
  next_action:
    | { type: 'review' }
    | { type: 'continue'; level_key: string }
    | { type: 'boss_or_mock' };
  pace_projection:
    | {
        kind: 'no_deadline';
        remaining_atoms: number;
        observed_atoms_per_day: number;
        readiness_ceiling_at_full_coverage: number;
        reachable: boolean;
        projected_ready_date: string | null;
        confidence: number;
      }
    | {
        kind: 'with_deadline';
        exam_date: string;
        days_remaining: number;
        remaining_atoms: number;
        readiness_ceiling_at_full_coverage: number;
        required_atoms_per_day: number | null;
        feasible: boolean;
        reason: 'exceeds_daily_budget' | 'mastery_ceiling_too_low' | null;
        confidence: number;
      };
}

/** Thrown on any non-2xx response, carrying the status and the backend's error code. */
export class DashboardApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`Dashboard API error ${status}: ${code}`);
    this.name = 'DashboardApiError';
  }
}

async function errorCodeFrom(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown };
    return typeof body.error === 'string' ? body.error : 'unknown_error';
  } catch {
    return 'unknown_error';
  }
}

function dashboardPath(ecosystem: string, route: string): string {
  const q = new URLSearchParams({ ecosystem, route }).toString();
  return `/v1/dashboard?${q}`;
}

/** GET the caller's composed dashboard (readiness, streak, exam T-minus, rank, next action). */
export async function fetchDashboard(
  idToken: string,
  identityId: string,
  params: { ecosystem: string; route: string },
  config: AuthConfig = authConfig,
  fetchImpl: FetchImpl = fetch,
): Promise<DashboardResponse> {
  const path = dashboardPath(params.ecosystem, params.route);
  const response = await authedFetch(
    { idToken, identityId, path, init: { method: 'GET' } },
    config,
    fetchImpl,
  );
  if (!response.ok) {
    throw new DashboardApiError(response.status, await errorCodeFrom(response));
  }
  return (await response.json()) as DashboardResponse;
}
